import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from '@/locales/en/common.json';
import enViewer from '@/locales/en/viewer.json';
import enTools from '@/locales/en/tools.json';
import enEsign from '@/locales/en/esign.json';
import enAuth from '@/locales/en/auth.json';
import enMarketing from '@/locales/en/marketing.json';

import frCommon from '@/locales/fr/common.json';
import frViewer from '@/locales/fr/viewer.json';
import frTools from '@/locales/fr/tools.json';
import frEsign from '@/locales/fr/esign.json';
import frAuth from '@/locales/fr/auth.json';
import frMarketing from '@/locales/fr/marketing.json';

import esCommon from '@/locales/es/common.json';
import esViewer from '@/locales/es/viewer.json';
import esTools from '@/locales/es/tools.json';
import esEsign from '@/locales/es/esign.json';
import esAuth from '@/locales/es/auth.json';
import esMarketing from '@/locales/es/marketing.json';

import deCommon from '@/locales/de/common.json';
import deViewer from '@/locales/de/viewer.json';
import deTools from '@/locales/de/tools.json';
import deEsign from '@/locales/de/esign.json';
import deAuth from '@/locales/de/auth.json';
import deMarketing from '@/locales/de/marketing.json';

import itCommon from '@/locales/it/common.json';
import itViewer from '@/locales/it/viewer.json';
import itTools from '@/locales/it/tools.json';
import itEsign from '@/locales/it/esign.json';
import itAuth from '@/locales/it/auth.json';
import itMarketing from '@/locales/it/marketing.json';

import zhCommon from '@/locales/zh/common.json';
import zhViewer from '@/locales/zh/viewer.json';
import zhTools from '@/locales/zh/tools.json';
import zhEsign from '@/locales/zh/esign.json';
import zhAuth from '@/locales/zh/auth.json';
import zhMarketing from '@/locales/zh/marketing.json';

import jaCommon from '@/locales/ja/common.json';
import jaViewer from '@/locales/ja/viewer.json';
import jaTools from '@/locales/ja/tools.json';
import jaEsign from '@/locales/ja/esign.json';
import jaAuth from '@/locales/ja/auth.json';
import jaMarketing from '@/locales/ja/marketing.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'it', name: 'Italian', native: 'Italiano' },
  { code: 'zh', name: 'Chinese (Simplified)', native: '简体中文' },
  { code: 'ja', name: 'Japanese', native: '日本語' },
];

export const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

const resources = {
  en: { common: enCommon, viewer: enViewer, tools: enTools, esign: enEsign, auth: enAuth, marketing: enMarketing },
  fr: { common: frCommon, viewer: frViewer, tools: frTools, esign: frEsign, auth: frAuth, marketing: frMarketing },
  es: { common: esCommon, viewer: esViewer, tools: esTools, esign: esEsign, auth: esAuth, marketing: esMarketing },
  de: { common: deCommon, viewer: deViewer, tools: deTools, esign: deEsign, auth: deAuth, marketing: deMarketing },
  it: { common: itCommon, viewer: itViewer, tools: itTools, esign: itEsign, auth: itAuth, marketing: itMarketing },
  zh: { common: zhCommon, viewer: zhViewer, tools: zhTools, esign: zhEsign, auth: zhAuth, marketing: zhMarketing },
  ja: { common: jaCommon, viewer: jaViewer, tools: jaTools, esign: jaEsign, auth: jaAuth, marketing: jaMarketing },
};

export const LANGUAGE_STORAGE_KEY = 'scrivar_preferred_language';

function detectInitialLanguage() {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage?.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_CODES.includes(stored)) return stored;
  } catch {
    // localStorage may be blocked — fall through to browser detection.
  }
  const nav = (window.navigator?.language || 'en').toLowerCase();
  const base = nav.split('-')[0];
  if (SUPPORTED_CODES.includes(base)) return base;
  return 'en';
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: detectInitialLanguage(),
    fallbackLng: 'en',
    ns: ['common', 'viewer', 'tools', 'esign', 'auth', 'marketing'],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

export function setLanguage(code) {
  if (!SUPPORTED_CODES.includes(code)) return;
  i18n.changeLanguage(code);
  try {
    window.localStorage?.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // ignore storage errors (private browsing, etc.)
  }
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', code);
  }
  if (typeof window !== 'undefined' && window.electronAPI?.setLanguage) {
    window.electronAPI.setLanguage(code).catch(() => {});
  }
}

export function getLanguage() {
  return i18n.language || 'en';
}

export default i18n;
